import React, { useState, useEffect } from 'react';
import { 
  Container, Grid, Paper, Typography, Button, Box, 
  IconButton, Card, CardContent, List, ListItem,
  ListItemText, ListItemIcon, Divider, Avatar,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  Accordion, AccordionSummary, AccordionDetails, Chip,
  LinearProgress, Tooltip
} from '@mui/material';
import {
  Create, Edit, Delete, MonetizationOn, School, 
  Assignment, CheckCircle, Schedule, Notifications,
  TrendingUp, People, AccessTime, Event, AttachMoney,
  ExpandMore, Timer, Assessment, Lightbulb, Warning,
  TrendingDown, ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { getInstructorMetrics, getRecommendations, getTestHistory } from './mockInstructorApi';
import CreateExamForm from './CreateTestForm';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const InstructorDashboard = ({ view = 'overview' }) => {
  const [metrics, setMetrics] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [examHistory, setExamHistory] = useState([]);
  const [createExamDialog, setCreateExamDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [metricsData, recommendationsData, historyData] = await Promise.all([
        getInstructorMetrics(),
        getRecommendations(),
        getTestHistory()
      ]);
      setMetrics(metricsData);
      setRecommendations(recommendationsData);
      setExamHistory(historyData);
    };
    fetchData();
  }, []);

  const ExamCard = ({ test }) => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6">{test.name}</Typography>
            <Typography variant="body2" color="textSecondary">{test.category}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box display="flex" alignItems="center">
              <People sx={{ mr: 1 }} />
              <Typography>{test.totalAttempts} attempts</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box display="flex" alignItems="center">
              <Assessment sx={{ mr: 1 }} />
              <Typography>{test.averageScore}% avg score</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box display="flex" alignItems="center">
              <Timer sx={{ mr: 1 }} />
              <Typography>{test.stats.averageTime} min avg time</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Chip 
              label={`${test.stats.completionRate}% completion`}
              color={test.stats.completionRate > 90 ? 'success' : 'warning'}
            />
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Difficulty Distribution</Typography>
              <Box>
                {Object.entries(test.stats.difficultyDistribution).map(([level, percentage]) => (
                  <Box key={level} mb={1}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{level}</Typography>
                      <Typography variant="body2">{percentage}%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={percentage} 
                      color={level === 'easy' ? 'success' : level === 'medium' ? 'warning' : 'error'}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Top Performing Questions</Typography>
              <List dense>
                {test.stats.topPerformingQuestions.map((q) => (
                  <ListItem key={q.id}>
                    <ListItemIcon>
                      <ArrowUpward color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={q.topic}
                      secondary={`${q.correctRate}% correct responses`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Challenging Questions</Typography>
              <List dense>
                {test.stats.challengingQuestions.map((q) => (
                  <ListItem key={q.id}>
                    <ListItemIcon>
                      <ArrowDownward color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={q.topic}
                      secondary={`${q.correctRate}% correct responses`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                sx={{ mr: 1 }}
              >
                Edit Exam
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
              >
                Delete Exam
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const RevenueChart = () => (
    <Paper elevation={3} sx={{ p: 2, mt: 3, height: 400 }}>
      <Typography variant="h6" gutterBottom>Monthly Revenue</Typography>
      <ResponsiveContainer width="100%" height="90%">
        <AreaChart data={metrics?.revenueData.monthly}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <RechartsTooltip />
          <Area type="monotone" dataKey="amount" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
        </AreaChart>
      </ResponsiveContainer>
    </Paper>
  );

  const StudentEngagementChart = () => (
    <Paper elevation={3} sx={{ p: 2, mt: 3, height: 400 }}>
      <Typography variant="h6" gutterBottom>Student Engagement</Typography>
      <ResponsiveContainer width="100%" height="90%">
        <LineChart data={metrics?.studentEngagement.daily}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <RechartsTooltip />
          <Line type="monotone" dataKey="activeStudents" stroke="#82ca9d" />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );

  const RecentActivity = () => (
    <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6" gutterBottom>Recent Activity</Typography>
      <List>
        {metrics?.recentActivity.map((activity, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemIcon>
                {activity.type === 'test_submission' && <Assignment color="primary" />}
                {activity.type === 'student_feedback' && <People color="success" />}
                {activity.type === 'earnings' && <AttachMoney color="warning" />}
                {activity.type === 'student_query' && <Notifications color="error" />}
              </ListItemIcon>
              <ListItemText 
                primary={activity.message}
                secondary={activity.time}
              />
            </ListItem>
            {index < metrics.recentActivity.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );

  const UpcomingSchedule = () => (
    <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6" gutterBottom>Upcoming Schedule</Typography>
      <List>
        {metrics?.upcomingSchedule.map((event, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemIcon>
                {event.type === 'test_deadline' && <Event color="error" />}
                {event.type === 'review_pending' && <Assignment color="warning" />}
                {event.type === 'new_test' && <Create color="success" />}
              </ListItemIcon>
              <ListItemText 
                primary={event.title}
                secondary={new Date(event.date).toLocaleDateString()}
              />
            </ListItem>
            {index < metrics.upcomingSchedule.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );

  const RecommendationsView = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>Trending Topics</Typography>
        <Grid container spacing={3}>
          {recommendations?.trendingTopics.map((topic) => (
            <Grid item xs={12} md={4} key={topic.topic}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>{topic.topic}</Typography>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Market Analysis</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><TrendingUp color="primary" /></ListItemIcon>
                      <ListItemText primary={`Growth: ${topic.marketAnalysis.demandGrowth}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><People color="primary" /></ListItemIcon>
                      <ListItemText primary={`Competition: ${topic.marketAnalysis.competitionLevel}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><MonetizationOn color="primary" /></ListItemIcon>
                      <ListItemText primary={`Avg Price: $${topic.marketAnalysis.averageTestPrice}`} />
                    </ListItem>
                  </List>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip 
                    icon={<TrendingUp />} 
                    label={`${topic.requestCount} requests`}
                    color="primary"
                  />
                  <Typography color="success.main" variant="h6">
                    ${topic.potentialEarnings}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>Gap Areas</Typography>
        <Grid container spacing={3}>
          {recommendations?.gapAreas.map((area) => (
            <Grid item xs={12} md={4} key={area.topic}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>{area.topic}</Typography>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Insights</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Assessment color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Market Potential"
                        secondary={area.insights.marketPotential}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Lightbulb color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Suggested Topics"
                        secondary={area.insights.suggestedTopics.join(', ')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Timer color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Preparation Time"
                        secondary={area.insights.estimatedPreparationTime}
                      />
                    </ListItem>
                  </List>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip 
                    icon={<Warning />} 
                    label={`${area.competitorCount} competitors`}
                    color="warning"
                  />
                  <Chip 
                    label={`Demand: ${area.demandScore}/10`}
                    color="success"
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>Student Feedback</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Topic</TableCell>
                <TableCell>Requests</TableCell>
                <TableCell>Common Feedback</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recommendations?.studentFeedback.map((feedback) => (
                <TableRow key={feedback.topic}>
                  <TableCell>{feedback.topic}</TableCell>
                  <TableCell>{feedback.requestCount}</TableCell>
                  <TableCell>{feedback.commonFeedback}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );

  const CreateExamDialog = () => (
    <Dialog 
      open={createExamDialog} 
      onClose={() => setCreateExamDialog(false)} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle>Create New Exam</DialogTitle>
      <DialogContent>
        <CreateExamForm onClose={() => setCreateExamDialog(false)} />
      </DialogContent>
    </Dialog>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {view === 'overview' && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Overview</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Create />}
              onClick={() => setCreateExamDialog(true)}
            >
              Submit questions
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Assignment color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Questions</Typography>
                </Box>
                <Typography variant="h4">{metrics?.questionsSubmitted}</Typography>
                <Typography variant="body2" color="textSecondary">Total Submitted</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <School color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Exams</Typography>
                </Box>
                <Typography variant="h4">{metrics?.testsSubmitted}</Typography>
                <Typography variant="body2" color="textSecondary">Exams Created</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <People color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Students</Typography>
                </Box>
                <Typography variant="h4">{metrics?.totalStudents}</Typography>
                <Typography variant="body2" color="textSecondary">Total Enrolled</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <MonetizationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Earnings</Typography>
                </Box>
                <Typography variant="h4">${metrics?.earnings}</Typography>
                <Typography variant="body2" color="textSecondary">Total Earnings</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <RevenueChart />
              <StudentEngagementChart />
            </Grid>
            <Grid item xs={12} md={4}>
              <RecentActivity />
              <UpcomingSchedule />
            </Grid>
          </Grid>
          <CreateExamDialog />
        </>
      )}

      {view === 'tests' && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Exams</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Create />}
              onClick={() => setCreateExamDialog(true)}
            >
              Submit questions
            </Button>
          </Box>
          <Box sx={{ mb: 3 }}>
            {examHistory.map((test) => (
              <ExamCard key={test.id} test={test} />
            ))}
          </Box>
          <CreateExamDialog />
        </>
      )}

      {view === 'recommendations' && <RecommendationsView />}
    </Container>
  );
};

export default InstructorDashboard;
